import Link from "@components/Link";
import type { HeaderNavigationLinkFragment } from "@server/gql/graphql";
import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/utils";
import { tv, VariantProps } from "tailwind-variants";

type UspItemProps = {
  item?: HeaderNavigationLinkFragment;
  locale?: string;
};

type UspItemLinkProps<T extends keyof HTMLElementTagNameMap = "span"> =
  ComponentPropsWithoutRef<T> &
    VariantProps<typeof uspItemVariants> &
    UspItemProps;

const uspItemVariants = tv({
  base: "flex-shrink-0 px-1 leading-none underline-offset-2",
  variants: {
    intent: {
      link: "hover:underline",
    },
  },
});

const USPItemLink = ({
  intent,
  className,
  item,
  locale,
  ...props
}: UspItemLinkProps<"a">) => {
  if (!item?.title) return null;

  const { internalLink } = item;

  if (internalLink && intent === "link") {
    return (
      <Link
        className={cn(
          uspItemVariants({ intent }),
          "flex items-center whitespace-nowrap",
          className,
        )}
        href={internalLink.slug}
        locale={locale}
        {...props}
      >
        {item.title}
      </Link>
    );
  }

  return (
    <USPItemText item={item} locale={locale} className={className} {...props} />
  );
};

const USPItemText = ({
  item,
  className,
  ...props
}: UspItemLinkProps<"span">) => {
  return (
    <span className={cn(uspItemVariants(), className)} {...props}>
      {item?.title}
    </span>
  );
};

const USPItem = ({ item, locale, ...props }: UspItemProps) => {
  if (!item) return null;

  const hasSlug = Boolean(item.internalLink?.slug);

  if (hasSlug) {
    return (
      <USPItemLink
        item={item}
        locale={locale}
        intent={hasSlug ? "link" : undefined}
        {...props}
      />
    );
  }

  return <USPItemText item={item} locale={locale} {...props} />;
};

export default USPItem;
